import { useEffect, Fragment } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useSocialLinks,
  usePropertyName,
  usePropertyField,
  usePropertyPhone,
  usePropertyAddressUrl,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  repliWhiteLogoUrl,
  MultihubLogoMonoWhite,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  LinkedInIcon,
  YouTubeIcon,
  TikTokIcon,
} from '@vibesites/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBanSmoking } from '@fortawesome/free-solid-svg-icons/faBanSmoking';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { onColorLinksCss } from '../utils/onColorLinks';
import { isUnitedStatesProperty } from '../utils/propertyCountry';

// ============================================================================
// Footer background
// ----------------------------------------------------------------------------
// The Aster footer sits on the property's primary brand color. White body text
// and column headings read against it via `var(--vibe-color-primary)`.
// ============================================================================

const FOOTER_BG = 'var(--vibe-color-primary)';

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — normalize casing and keep the period glued to the time.
  if (/[ap]m/i.test(trimmed)) {
    return trimmed
      .replace(/([ap]m)/gi, (m) => m.toLowerCase())
      .replace(/\s+([ap]m)\b/g, '\u00A0$1');
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  // Non-breaking space keeps "6:00 pm" from wrapping mid-period.
  return `${hours}:${minutes}\u00A0${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided hours by canonical day. An entry that exists but has no
  // open/close times is treated as explicitly "Closed".
  const hoursByDay = new Map<string, string>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!DAY_ORDER.includes(key)) continue;
    if (hoursByDay.has(key)) continue;
    const isClosed =
      (!hasValue(entry.open_time) && !hasValue(entry.close_time)) ||
      /closed/i.test(entry.open_time || '') ||
      /closed/i.test(entry.close_time || '');
    hoursByDay.set(
      key,
      isClosed
        ? 'Closed'
        : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`,
    );
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week in canonical order so days with no hours
  // provided still render as "Closed" rather than being dropped from the list.
  for (const key of DAY_ORDER) {
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const hoursStr = hoursByDay.get(key) ?? 'Closed';

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// Shared label styling for the four column headings (white, uppercase, tracked).
const COLUMN_LABEL_CLASS =
  'font-mono text-sm uppercase tracking-[0.2em] text-white mb-4';

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const logoSlotDef = contentSchema.slotGroups!['footer.logo'];
  const designByLogoDef = contentSchema.slotGroups!['footer.design_by_logo'];
  const managedByLogoDef = contentSchema.slotGroups!['footer.managed_by_logo'];
  const poweredByLogoDef = contentSchema.slotGroups!['footer.powered_by_logo'];
  const addressLinkDef = contentSchema.slotGroups!['footer.address'];
  const accessibilityLinkDef = contentSchema.slotGroups!['footer.accessibility_link'];
  const privacyPolicyLinkDef = contentSchema.slotGroups!['footer.privacy_policy_link'];
  const sitemapLinkDef = contentSchema.slotGroups!['footer.sitemap_link'];
  const propertyName = usePropertyName();
  const country = usePropertyField('property_address_country');
  const state = usePropertyField('property_address_state');
  // Equal Housing Opportunity is a US fair-housing mark — hidden for Canada
  // and every other country.
  const showFairHousing = isUnitedStatesProperty(country, state);
  const phoneToken = useContentToken('footer.phone');
  const trackedPhone = usePropertyPhone();
  const phone = phoneToken || trackedPhone || '(555) 555-5555';

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const companyName = usePropertyField('property_company_name');
  const companyUrl = usePropertyField('property_company_url');
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const social = useSocialLinks();
  const socialItems = [
    { url: social.facebook, Icon: FacebookIcon, label: 'Facebook' },
    { url: social.instagram, Icon: InstagramIcon, label: 'Instagram' },
    { url: social.twitter, Icon: TwitterIcon, label: 'Twitter' },
    { url: social.linkedin, Icon: LinkedInIcon, label: 'LinkedIn' },
    { url: social.youtube, Icon: YouTubeIcon, label: 'YouTube' },
    { url: social.tiktok, Icon: TikTokIcon, label: 'TikTok' },
  ].filter((s) => hasValue(s.url));

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe Connect launcher z-index pin ─────────────────────────────────
  // The launcher is injected at runtime and then writes its own inline
  // `style="z-index: …"`, which beats a normal stylesheet rule. `setProperty`
  // with the `important` priority is what makes that inline value lose.
  //
  // Only the real launcher is touched:
  //   .rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle
  // An outermost `position: fixed` ancestor is pinned only when that ancestor
  // is the chat wrap, so a surrounding page shell is left alone. The value
  // sits just above the open header (bar z-50, mobile drawer z-71, menus z-70).
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        if (getComputedStyle(cur).position === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement) || !el.matches(LAUNCHER_SELECTOR)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // querySelectorAll skips the root node itself.
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Class/id so a node that becomes the launcher is caught; style so a
      // later inline z-index from the vendor is overwritten again.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer className="site-footer text-white" style={{ backgroundColor: FOOTER_BG }}>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ---- Community Logo Wordmark ---- */}
        <div className="pt-12 pb-8 md:pt-16 text-center">
          <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
            <a href="/" className="inline-block">
              {logo.image ? (
                <VibesitesImage
                  url={logo.image}
                  alt={logo.alt || propertyName || 'Community Logo'}
                  sizes="(min-width: 768px) 240px, 180px"
                  className="h-12 md:h-16 w-auto max-w-[240px] object-contain mx-auto"
                />
              ) : (
                <span className="font-heading uppercase tracking-wide font-light text-3xl md:text-5xl text-[var(--vibe-color-text-on-primary)] leading-none">
                  {propertyName || 'Our Community'}
                </span>
              )}
            </a>
          </EditableSlotGroup>
        </div>

        {/* ---- Info Columns: Contact / Visit Us / Office Hours / Follow Us ----
            Stay 2-up through tablet; 4-up only at lg+ so nowrap hours don't
            collide with the address / social columns on mid-width viewports. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 pt-4 pb-10 text-center">

          {/* Contact */}
          <div className="flex flex-col items-center min-w-0">
            <RichTextContent path="footer.contact_label" defaultValue="<p>Contact</p>" as="p" className={COLUMN_LABEL_CLASS} />
            <div className="space-y-2 font-mono text-sm text-white/85">
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref block hover:text-white transition-colors"
              >
                <EditableToken
                  path="footer.phone"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>

          {/* Visit Us */}
          <div className="flex flex-col items-center min-w-0">
            <RichTextContent path="footer.visit_us_label" defaultValue="<p>Visit Us</p>" as="p" className={COLUMN_LABEL_CLASS} />
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="block font-mono text-sm text-white/85 hover:text-white transition-colors w-full max-w-[220px] mx-auto text-center lg:text-left break-words"
              >
                {addressLink.text || '123 Main Street, City, ST 00000'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Office Hours */}
          <div className="flex flex-col items-center min-w-0">
            <RichTextContent path="footer.office_hours_label" defaultValue="<p>Office Hours</p>" as="p" className={COLUMN_LABEL_CLASS} />
            {groupedHours.length > 0 ? (
              <div className="inline-grid grid-cols-[auto_auto] gap-x-4 gap-y-2 font-mono text-sm text-white/85 text-left max-w-full">
                {groupedHours.map((group, index) => (
                  <Fragment key={index}>
                    <span className="whitespace-nowrap">{group.shortLabel}</span>
                    {/* nowrap keeps am/pm with the range; full row stays in-column
                        because we don't go 4-up until lg where there's room. */}
                    <span className="whitespace-nowrap">{group.hours}</span>
                  </Fragment>
                ))}
              </div>
            ) : (
              <p className="font-mono text-sm text-white/40">Office hours will be displayed here.</p>
            )}
          </div>

          {/* Follow Us */}
          <div className="flex flex-col items-center min-w-0">
            <RichTextContent path="footer.follow_us_label" defaultValue="<p>Follow Us</p>" as="p" className={COLUMN_LABEL_CLASS} />
            {socialItems.length > 0 ? (
              <div className="flex flex-wrap items-center justify-center gap-4 text-white/80">
                {socialItems.map(({ url, Icon, label }) => (
                  <a
                    key={label}
                    href={url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="hover:text-white transition-colors"
                  >
                    <Icon size={20} color="currentColor" />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

        </div>

        {/* ---- Compliance Icons ---- */}
        <div className="flex items-center justify-center gap-6 text-white/80 pb-5">
          {showFairHousing ? <FairHousingIcon size={30} color="currentColor" /> : null}
          <PetsAllowedIcon size={30} color="currentColor" />
          <WheelchairIcon size={30} color="currentColor" />
          <FontAwesomeIcon
            icon={faBanSmoking}
            style={{ width: 30, height: 30 }}
            color="currentColor"
            aria-label="No Smoking"
            role="img"
          />
        </div>

        {/* ---- Legal Nav + Copyright (sit directly under the compliance icons) ---- */}
        <div className="flex flex-col items-center gap-2 pb-8 text-center font-mono text-xs">
          <nav
            className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[var(--vibe-color-text-on-primary)]"
            aria-label="Legal links"
          >
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-white transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-white/30">|</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-white transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-white/30">|</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-white transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
          </nav>

          {/* Copyright + community name (year auto-updates from the runtime clock
              so the footer never goes stale). */}
          <div className="text-white/70">
            © {new Date().getFullYear()} All Rights Reserved{' '}
            <span aria-hidden="true" className="text-white/40">|</span>{' '}
            <span>{propertyName || 'Community Name'}</span>
          </div>
        </div>

        {/* ---- Divider ---- */}
        <div className="border-t border-white/15" />

        {/* ---- Bottom Bar: Managed By / Powered By / Website Design By ----
            Three columns on desktop so the center attribution stays truly
            centered regardless of how wide the flanking logos are; stacks
            vertically on mobile. */}
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-y-4 gap-x-6 py-6 font-mono text-xs">

          {/* Managed By (left) */}
          <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
            <a
              href={managedByLogo.url || companyUrl || '#'}
              target={managedByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center justify-center md:justify-start gap-3 text-white/70 hover:text-white transition-colors"
            >
              <RichTextContent
                path="footer.managed_by_text"
                defaultValue="<p>Managed By</p>"
                as="span"
              />
              {managedByLogo.image ? (
                <VibesitesImage
                  url={managedByLogo.image}
                  alt={managedByLogo.alt || companyName || 'Management Company Logo'}
                  height={44}
                  sizes="180px"
                  className="h-11 w-auto max-w-[180px] object-contain"
                />
              ) : companyName ? (
                <span className="text-sm font-semibold text-white">{companyName}</span>
              ) : null}
            </a>
          </EditableSlotGroup>

          {/* Powered by multihub (center) */}
          <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
            <a
              href={poweredByLogo.url || 'https://www.multihub.com/'}
              target={poweredByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <RichTextContent
                path="footer.powered_by_text"
                defaultValue="<p>Powered by</p>"
                as="span"
              />
              {poweredByLogo.image ? (
                <VibesitesImage
                  url={poweredByLogo.image}
                  alt={poweredByLogo.alt || 'Multihub Logo'}
                  height={20}
                  sizes="130px"
                  className="h-5 w-auto"
                />
              ) : (
                <MultihubLogoMonoWhite height={16} alt={poweredByLogo.alt || 'Multihub'} />
              )}
            </a>
          </EditableSlotGroup>

          {/* Website Design By (Repli) */}
          <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center justify-center md:justify-end gap-2 text-white/70 hover:text-white transition-colors"
            >
              <RichTextContent
                path="footer.design_by_text"
                defaultValue="<p>Website Design by</p>"
                as="span"
              />
              <VibesitesImage
                url={repliWhiteLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={16}
                sizes="90px"
                className="h-4 w-auto"
                priority
              />
            </a>
          </EditableSlotGroup>

        </div>
      </div>

      {/* ---- Launcher z-index fallback ----
          Cheap first pass for the real RentCafe Connect launcher. The
          `useEffect` above re-applies the same selectors with inline
          `!important` after the vendor writes its own z-index. */}
      <style>{`
        ${onColorLinksCss('.site-footer [data-vibesite-richtext]')}
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact</p>',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>Visit Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.follow_us_label': {
      label: 'Follow Us Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Managed By</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
  },
  slotGroups: {
    'footer.logo': {
      label: 'Footer Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'footer.design_by_logo': {
      label: 'Website Design By Logo',
      fields: {
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
        url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'footer.managed_by_logo': {
      label: 'Managed By Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_icon'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Management Company Logo' },
        // URL has no defaultField — falls back to the property's company URL at
        // render time. An explicit override from the editor still takes precedence.
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'footer.powered_by_logo': {
      label: 'Powered By Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub Logo' },
        url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.com/' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'footer.address': {
      label: 'Address',
      fields: {
        text: { label: 'Address Text', type: 'text', defaultValue: '280 Main St, Lucan, ON N0M 2J0, Canada' },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at render
        // time (mobile-safe Google Maps URL built from place/business/address
        // fields). An explicit override from the editor still takes precedence.
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'footer.accessibility_link': {
      label: 'Accessibility Link',
      fields: {
        text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
        url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'footer.privacy_policy_link': {
      label: 'Privacy Policy Link',
      fields: {
        text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
        url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'footer.sitemap_link': {
      label: 'Sitemap Link',
      fields: {
        text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
        url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
